import { MutationStatus } from '@tanstack/react-query';
import { XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { StaticHeadersForm } from '@/components/form/StaticHeadersForm';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import {
  type ApiKeyFormData,
  type BasicCredentialFormData,
  type BearerTokenFormData,
  COMPLEX_LOGIN_FORM_INITIAL,
  type ComplexLoginFormData,
  type ComplexLoginMultiFormData,
  CredentialFormBody,
  type CredentialSubmitPayload,
  CredentialType,
  CredentialTypeSelection,
  IdentitySetManager,
  type RecordingCredentialFormData,
  type StaticTokenFormData,
  validateApiKeyFormData,
  validateBasicFormData,
  validateBearerTokenFormData,
  validateComplexLoginMulti,
  validateRecordingFormData,
  validateStaticTokenFormData,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import { RecipeWizard } from '@/sections/asset/components/tabs/credentials/RecipeWizard';
import {
  RecorderHost,
  type RecorderResult,
} from '@/sections/asset/components/tabs/credentials/RecorderProgress';

// Stable no-op setter for CredentialFormBody's required (but unused for
// COMPLEX_LOGIN) complexLoginForm prop. COMPLEX_LOGIN renders IdentitySetManager
// instead and never reaches CredentialFormBody's COMPLEX_LOGIN switch arm, so
// this is never invoked — mirrors the wizard's CredentialFormStep.
const noopSetComplexLoginForm: React.Dispatch<
  React.SetStateAction<ComplexLoginFormData>
> = () => {};

export interface AddCredentialModalProps {
  isWebService: boolean;
  isOpen: boolean;
  onClose: () => void;
  modalStep: 'type' | 'form' | 'progress';
  setModalStep: (step: 'type' | 'form' | 'progress') => void;
  selectedType: CredentialType;
  onTypeChange: (type: CredentialType) => void;
  basicForm: BasicCredentialFormData;
  setBasicForm: React.Dispatch<React.SetStateAction<BasicCredentialFormData>>;
  recordingForm: RecordingCredentialFormData;
  setRecordingForm: React.Dispatch<
    React.SetStateAction<RecordingCredentialFormData>
  >;
  bearerTokenForm: BearerTokenFormData;
  setBearerTokenForm: React.Dispatch<React.SetStateAction<BearerTokenFormData>>;
  apiKeyForm: ApiKeyFormData;
  setApiKeyForm: React.Dispatch<React.SetStateAction<ApiKeyFormData>>;
  staticTokenForm: StaticTokenFormData;
  setStaticTokenForm: React.Dispatch<React.SetStateAction<StaticTokenFormData>>;
  complexLoginMulti: ComplexLoginMultiFormData;
  setComplexLoginMulti: React.Dispatch<
    React.SetStateAction<ComplexLoginMultiFormData>
  >;
  webAppUrl: string;
  // Non-complex types submit via onSubmit (single broker POST); COMPLEX_LOGIN
  // fans out N identities via onSubmitComplexLogin, owned by the host.
  onSubmit: (payload: CredentialSubmitPayload) => Promise<void>;
  onSubmitComplexLogin: () => Promise<void>;
  addCredentialStatus: MutationStatus;
  complexSubmitStatus: MutationStatus;
  canAddCredentials?: boolean;
  addDisabledTooltip?: string;
  // Recorder-progress mode: one RecorderHost per succeeded identity (stacked),
  // plus a per-identity failure list for identities that never spawned a
  // recorder (LOCKED #3 — no silent single-recorder watch).
  recorderEntries: { id: string; label: string; conversationId: string }[];
  failedIdentities: { id: string; label: string }[];
  onRecorderComplete: (result: RecorderResult) => void;
  onBackToList?: () => void;
}

export function AddCredentialModal({
  isWebService,
  isOpen,
  onClose,
  modalStep,
  setModalStep,
  selectedType,
  onTypeChange,
  basicForm,
  setBasicForm,
  recordingForm,
  setRecordingForm,
  bearerTokenForm,
  setBearerTokenForm,
  apiKeyForm,
  setApiKeyForm,
  staticTokenForm,
  setStaticTokenForm,
  complexLoginMulti,
  setComplexLoginMulti,
  webAppUrl,
  onSubmit,
  onSubmitComplexLogin,
  addCredentialStatus,
  complexSubmitStatus,
  canAddCredentials = true,
  addDisabledTooltip,
  recorderEntries,
  failedIdentities,
  onRecorderComplete,
  onBackToList,
}: AddCredentialModalProps) {
  // Tracks whether the user has attempted to submit an invalid identity set so
  // IdentitySetManager can promote incomplete → error styling. React Compiler
  // memoizes — no useMemo for multiValidation (per ui/CLAUDE.md).
  const [attempted, setAttempted] = useState(false);
  const multiValidation = validateComplexLoginMulti(complexLoginMulti);

  const handleSubmit = async () => {
    if (modalStep === 'type') {
      setModalStep('form');
      return;
    }

    // Complex-Login now fans out N identities from the single 'form' step's
    // IdentitySetManager. The host owns the actual submit (onSubmitComplexLogin);
    // this only gates on the multi-set validation and surfaces per-identity
    // errors inline (not toast-only).
    if (selectedType === CredentialType.COMPLEX_LOGIN) {
      if (!multiValidation.ok) {
        setAttempted(true);
        toast.error(
          multiValidation.formError ?? 'Complete all logins before submitting'
        );
        return;
      }
      await onSubmitComplexLogin();
      return;
    }

    // All other types submit from the 'form' step via the switch below.
    let error: string | null = null;
    let payload: CredentialSubmitPayload | null = null;

    switch (selectedType) {
      case CredentialType.BASIC:
        error = validateBasicFormData(basicForm);
        if (!error) {
          payload = { type: CredentialType.BASIC, data: basicForm };
        }
        break;
      case CredentialType.RECORDING:
        error = validateRecordingFormData(recordingForm);
        if (!error && recordingForm.file) {
          payload = {
            type: CredentialType.RECORDING,
            data: { ...recordingForm, file: recordingForm.file },
          };
        }
        break;
      case CredentialType.BEARER_TOKEN:
        error = validateBearerTokenFormData(bearerTokenForm);
        if (!error) {
          payload = {
            type: CredentialType.BEARER_TOKEN,
            data: bearerTokenForm,
          };
        }
        break;
      case CredentialType.API_KEY:
        error = validateApiKeyFormData(apiKeyForm);
        if (!error) {
          payload = { type: CredentialType.API_KEY, data: apiKeyForm };
        }
        break;
      case CredentialType.STATIC_TOKEN:
        error = validateStaticTokenFormData(staticTokenForm);
        if (!error) {
          payload = {
            type: CredentialType.STATIC_TOKEN,
            data: staticTokenForm,
          };
        }
        break;
      case CredentialType.RECIPE:
        // Recipe path is fully owned by the wizard (validation + submit happen
        // there via its Save Recipe button). This switch arm should never fire
        // because handleSubmit is wired to the modal's footer, and we hide
        // that footer for the recipe wizard.
        return;
      default:
        break;
    }

    if (error) {
      toast.error(error);
      return;
    }

    if (payload) {
      await onSubmit(payload);
    }
  };

  const handleBack = () => {
    if (modalStep === 'form') {
      setModalStep('type');
    }
  };

  const getModalTitle = () => {
    if (modalStep === 'type') {
      return 'Step 1: Select Authentication Method';
    }
    if (modalStep === 'progress') {
      return 'Recording Login';
    }

    switch (selectedType) {
      case CredentialType.BASIC:
        return 'Step 2: Enter Credentials';
      case CredentialType.RECORDING:
        return 'Step 2: Upload Login Recording';
      case CredentialType.BEARER_TOKEN:
        return 'Step 2: Configure Bearer Token';
      case CredentialType.API_KEY:
        return 'Step 2: Configure API Key';
      case CredentialType.STATIC_TOKEN:
        return 'Step 2: Configure Static Headers';
      case CredentialType.COMPLEX_LOGIN:
        return 'Step 2: Configure Complex Login';
      case CredentialType.RECIPE:
        // No "Step 2:" prefix — the wizard renders its own step indicator
        // (Step X of 5), so prefixing with the modal's outer step would
        // double-count and confuse the user.
        return 'Build Login Recipe';
      default:
        return 'Add Credential';
    }
  };

  const getFooterText = () => {
    switch (modalStep) {
      case 'type':
        return 'Next';
      case 'form':
        // Every type — including Complex-Login, which now fans out its
        // identities from this single 'form' step — submits from here.
        return 'Add Credential';
      default:
        return 'Next';
    }
  };

  // Recipe credential is built via a multi-step wizard that owns its own
  // progress bar, validation, navigation, and submit button. The modal's
  // built-in footer is hidden in that mode so we don't show duplicate
  // footers / progress indicators.
  const isRecipeWizard =
    modalStep === 'form' && selectedType === CredentialType.RECIPE;
  // Progress mode renders the recorder timeline with its own footer (a
  // single Close button enabled only after finalize lands).
  const isProgressMode = modalStep === 'progress';
  const hideBuiltinFooter = isRecipeWizard || isProgressMode;

  const isComplexForm =
    modalStep === 'form' && selectedType === CredentialType.COMPLEX_LOGIN;
  // Complex-Login's master/detail identity manager (and the stacked progress
  // view) need the wider 3xl modal; everything else stays xl.
  const isSubmitPending =
    addCredentialStatus === 'pending' || complexSubmitStatus === 'pending';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size={isComplexForm || isProgressMode ? '3xl' : 'xl'}
      hideFooter={hideBuiltinFooter}
      footer={
        hideBuiltinFooter
          ? undefined
          : {
              className: 'w-fit',
              text: getFooterText(),
              onClick: handleSubmit,
              isLoading: isSubmitPending,
              disabled:
                !canAddCredentials ||
                (modalStep === 'type' && !selectedType) ||
                isSubmitPending,
              disabledTooltip: !canAddCredentials
                ? addDisabledTooltip
                : undefined,
              left:
                modalStep === 'form' ? (
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleBack}
                      variant="ghost"
                      size="lg"
                      disabled={isSubmitPending}
                    >
                      Back
                    </Button>
                    {isComplexForm && (
                      <span className="text-chariot-text-secondary text-sm">
                        {multiValidation.readyCount} of{' '}
                        {complexLoginMulti.identities.length} ready
                      </span>
                    )}
                  </div>
                ) : modalStep === 'type' && onBackToList ? (
                  <Button onClick={onBackToList} variant="ghost" size="lg">
                    Back
                  </Button>
                ) : undefined,
            }
      }
    >
      {modalStep === 'type' && (
        <CredentialTypeSelection
          isWebService={isWebService}
          selectedType={selectedType}
          onTypeChange={onTypeChange}
        />
      )}

      {modalStep === 'form' &&
        selectedType !== CredentialType.STATIC_TOKEN &&
        selectedType !== CredentialType.RECIPE &&
        selectedType !== CredentialType.COMPLEX_LOGIN && (
          <CredentialFormBody
            selectedType={selectedType}
            basicForm={basicForm}
            setBasicForm={setBasicForm}
            recordingForm={recordingForm}
            setRecordingForm={setRecordingForm}
            bearerTokenForm={bearerTokenForm}
            setBearerTokenForm={setBearerTokenForm}
            apiKeyForm={apiKeyForm}
            setApiKeyForm={setApiKeyForm}
            // COMPLEX_LOGIN renders IdentitySetManager below and never reaches
            // CredentialFormBody's COMPLEX_LOGIN arm, but the props are still
            // required — a stable placeholder + no-op setter satisfies the type
            // without any single-form state (mirrors the wizard's
            // CredentialFormStep).
            complexLoginForm={COMPLEX_LOGIN_FORM_INITIAL}
            setComplexLoginForm={noopSetComplexLoginForm}
          />
        )}

      {/* Complex-Login: the single-screen master/detail identity manager,
          fanning out N identities over this one web application. */}
      {isComplexForm && (
        <IdentitySetManager
          value={complexLoginMulti}
          onChange={setComplexLoginMulti}
          webAppUrl={webAppUrl}
          validation={multiValidation}
          attempted={attempted}
        />
      )}

      {modalStep === 'form' && selectedType === CredentialType.STATIC_TOKEN && (
        <StaticHeadersForm
          label={staticTokenForm.label}
          onLabelChange={label =>
            setStaticTokenForm(prev => ({ ...prev, label }))
          }
          headers={staticTokenForm.headers}
          onHeadersChange={headers =>
            setStaticTokenForm(prev => ({ ...prev, headers }))
          }
        />
      )}

      {isRecipeWizard && (
        <RecipeWizard
          webAppUrl={webAppUrl}
          isSubmitting={addCredentialStatus === 'pending'}
          onClose={onClose}
          onSubmit={recipe =>
            onSubmit({ type: CredentialType.RECIPE, data: recipe })
          }
        />
      )}

      {isProgressMode && (
        <div className="space-y-6">
          {failedIdentities.length > 0 && (
            <div
              role="alert"
              aria-live="assertive"
              className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-3 pl-4"
            >
              <div className="text-error text-xs">
                {failedIdentities.length} login
                {failedIdentities.length > 1 ? 's' : ''} failed to start a
                recorder
              </div>
              <ul className="mt-2 space-y-1">
                {failedIdentities.map(f => (
                  <li
                    key={f.id}
                    className="text-chariot-text-secondary flex items-center gap-2 text-xs"
                  >
                    <XCircle className="text-chariot-stroke-critical size-4 shrink-0" />
                    <span className="truncate">
                      {f.label.trim() || 'Untitled'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {recorderEntries.map(r => (
            <RecorderHost
              key={r.id}
              conversationId={r.conversationId}
              label={r.label}
              onComplete={onRecorderComplete}
              closePolicy="always-enabled"
              onClose={onClose}
            />
          ))}
          {recorderEntries.length === 0 && (
            <div className="flex justify-end">
              <Button onClick={onClose} size="lg">
                Close
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
